import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import ReferenceListRail from "../components/Settings/ProductSettings/ReferenceListRail";
import ReferenceListPane from "../components/Settings/ProductSettings/ReferenceListPane";
import FabricPatternsPane from "../components/Settings/ProductSettings/FabricPatternsPane";
import ProductFormationsPane from "../components/Settings/ProductSettings/ProductFormationsPane";
import { useProductTypes } from "../hooks/useProductTypes";
import { useFabricTypes } from "../hooks/useFabricTypes";
import { useProductFormations } from "../hooks/useProductFormations";
import { useDeliveryChannels } from "../hooks/useDeliveryChannels";
import { useStockLocations } from "../hooks/useStockLocations";
import { useFabrics } from "../hooks/useFabrics";
import "../components/Settings/ProductSettings/ProductSettings.css";

/**
 * Product Settings — master–detail (v3).
 *
 * The seven reference lists used to sit side by side as cards, which meant one
 * page-wide filter running across all of them and seven independent scroll
 * regions. This version opens one list at a time: the rail on the left names
 * them all and keeps their counts, the pane on the right belongs entirely to
 * whichever is open. That gives every list one search box, one scroll region
 * and room for its own quirks as pane-local controls rather than as explanatory
 * copy in a footer.
 *
 * The page deliberately has no title of its own — the shell header already says
 * "Product Settings" — and no page padding, because the two panels are the
 * page.
 */

/** Which pane component a list is rendered by, and the chrome it needs. */
const LIST_ORDER = [
  { key: "fabricTypes", title: "Fabric Types", marker: "square" },
  { key: "productTypes", title: "Product Types", marker: "square" },
  { key: "productFormations", title: "Product Formations", marker: "square" },
  { key: "deliveryChannels", title: "Delivery Channels", marker: "square" },
  { key: "stockLocations", title: "Stock Locations", marker: "square" },
  // The two below the divider are the ones that do not behave like the rest.
  { key: "fabricPatterns", title: "Fabric Patterns", marker: "hatch", dividerBefore: true },
  { key: "fabricColours", title: "Fabric Colours", marker: "circle" },
];

/** Copy that belongs to a list rather than to the page. */
const LIST_COPY = {
  fabricTypes: {
    singular: "fabric type",
    emptyTitle: "No fabric types yet",
    emptyBody: "Fabrics are the materials a product is made from — patterns hang off them, so this list comes first.",
    addLabel: "Add fabric type",
  },
  productTypes: {
    singular: "product type",
    emptyTitle: "No product types yet",
    emptyBody: "The order form groups its lines by product type, so its picker stays empty until one exists.",
    addLabel: "Add product type",
  },
  productFormations: {
    singular: "product formation",
    emptyTitle: "No product formations yet",
    emptyBody: "Formations are the cuts and set shapes a line is made in.",
    addLabel: "Add formation",
  },
  deliveryChannels: {
    singular: "delivery channel",
    emptyTitle: "No delivery channels yet",
    emptyBody: "The order form’s delivery picker stays empty until one exists.",
    addLabel: "Add the first channel",
  },
  stockLocations: {
    singular: "stock location",
    emptyTitle: "No stock locations yet",
    emptyBody: "Ready stock is filed by location, so stock cannot be recorded until one exists.",
    addLabel: "Add stock location",
  },
  fabricColours: {
    singular: "colour",
    emptyTitle: "No colours yet",
    emptyBody: "Colours are searched on the server, so this list stays short until one is added.",
    addLabel: "Add colour",
  },
};

const ProductSettings = () => {
  // -- data hooks -------------------------------------------------------------
  const { productTypes, productTypesLoading, fetchProductTypes } =
    useProductTypes();
  const { fabricTypes, fabricTypesLoading, fetchFabricTypes } =
    useFabricTypes();
  const {
    productFormations,
    formationsByType,
    activeFormations,
    productFormationsLoading,
    fetchProductFormations,
  } = useProductFormations({ includeLegacy: true });
  const { deliveryChannels, deliveryChannelsLoading, fetchDeliveryChannels } =
    useDeliveryChannels();
  const { stockLocations, stockLocationsLoading, fetchStockLocations } =
    useStockLocations();
  const { fabrics, fabricsLoading, fetchFabrics, loadMore, hasMore, totalCount: fabricsTotal } =
    useFabrics({ includeStoreOnly: true });

  // -- which list is open -----------------------------------------------------
  // Product Types opens first: it is the list the order form leans on hardest
  // and the one most often edited.
  const [selected, setSelected] = useState("productTypes");

  // -- search, scoped to the open list ----------------------------------------
  // One box, not seven. It is cleared on every switch because a term typed for
  // one list almost never means anything in the next, and a stale filter that
  // hides every row reads as an empty list.
  const [search, setSearch] = useState("");
  const searchInputRef = useRef(null);

  const handleSelect = useCallback((key) => {
    setSelected(key);
    setSearch("");
  }, []);

  // "/" focuses the search box, matching the hint rendered inside it.
  useEffect(() => {
    const handler = (e) => {
      if (
        e.key === "/" &&
        document.activeElement !== searchInputRef.current &&
        !["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)
      ) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // -- fabric colours search is the server's job ------------------------------
  // The other lists are held in full in memory and filter as you type; colours
  // run to thousands, so the same box debounces into a request instead.
  const fabricSearchTimeout = useRef(null);

  const handleFabricSearch = useCallback(
    (value) => {
      if (fabricSearchTimeout.current) clearTimeout(fabricSearchTimeout.current);
      fabricSearchTimeout.current = setTimeout(() => {
        fetchFabrics(1, value || null);
      }, 400);
    },
    [fetchFabrics]
  );

  // Fire the debounced server search whenever the shared box changes while
  // colours are open, and clear the server-side term on the way out.
  useEffect(() => {
    if (selected !== "fabricColours") return;
    handleFabricSearch(search);
    return () => {
      if (fabricSearchTimeout.current) clearTimeout(fabricSearchTimeout.current);
    };
  }, [search, selected, handleFabricSearch]);

  // -- per-list wiring --------------------------------------------------------
  const listData = useMemo(
    () => ({
      fabricTypes: { data: fabricTypes, loading: fabricTypesLoading, fetch: fetchFabricTypes },
      productTypes: { data: productTypes, loading: productTypesLoading, fetch: fetchProductTypes },
      productFormations: { data: productFormations, loading: productFormationsLoading, fetch: fetchProductFormations },
      deliveryChannels: { data: deliveryChannels, loading: deliveryChannelsLoading, fetch: fetchDeliveryChannels },
      stockLocations: { data: stockLocations, loading: stockLocationsLoading, fetch: fetchStockLocations },
      fabricColours: { data: fabrics, loading: fabricsLoading, fetch: fetchFabrics },
    }),
    [
      fabricTypes, fabricTypesLoading, fetchFabricTypes,
      productTypes, productTypesLoading, fetchProductTypes,
      productFormations, productFormationsLoading, fetchProductFormations,
      deliveryChannels, deliveryChannelsLoading, fetchDeliveryChannels,
      stockLocations, stockLocationsLoading, fetchStockLocations,
      fabrics, fabricsLoading, fetchFabrics,
    ]
  );

  // Patterns count themselves inside their own pane — the list holds one
  // fabric's prints at a time — so the rail is told the total separately.
  const [patternsTotal, setPatternsTotal] = useState(null);

  const railLists = LIST_ORDER.map((list) => ({
    ...list,
    count:
      list.key === "fabricPatterns"
        ? patternsTotal
        : list.key === "fabricColours"
        ? fabricsTotal || fabrics.length
        : list.key === "productFormations"
        ? activeFormations.length
        : listData[list.key].data.length,
  }));

  const isPatterns = selected === "fabricPatterns";
  const isFormations = selected === "productFormations";
  const isColours = selected === "fabricColours";
  const current = LIST_ORDER.find((l) => l.key === selected);

  return (
    <div className="ps-page">
      <div className="ps-shell">
        <ReferenceListRail
          lists={railLists}
          selected={selected}
          onSelect={handleSelect}
        />

        {isPatterns ? (
          <FabricPatternsPane
            fabricTypes={fabricTypes}
            fabricTypesLoading={fabricTypesLoading}
            search={search}
            onSearchChange={setSearch}
            searchInputRef={searchInputRef}
            onTotalChange={setPatternsTotal}
          />
        ) : isFormations ? (
          <ProductFormationsPane
            productTypes={productTypes}
            productTypesLoading={productTypesLoading}
            search={search}
            onSearchChange={setSearch}
            searchInputRef={searchInputRef}
            productFormations={productFormations}
            formationsByType={formationsByType}
            productFormationsLoading={productFormationsLoading}
            fetchProductFormations={fetchProductFormations}
          />
        ) : (
          <ReferenceListPane
            settingsKey={selected}
            // Colours are titled and keyed for their own copy, but the server
            // has always served them under `settings/fabrics`.
            apiKey={isColours ? "fabrics" : selected}
            title={current.title}
            copy={LIST_COPY[selected]}
            data={listData[selected].data}
            loading={listData[selected].loading}
            fetch={listData[selected].fetch}
            search={search}
            onSearchChange={setSearch}
            searchInputRef={searchInputRef}
            // Colours-only: the count reads "N of TOTAL", the search hits the
            // server, the footer loads more, and there is no rename.
            serverSearch={isColours}
            totalCount={isColours ? fabricsTotal || fabrics.length : undefined}
            loadMore={isColours ? loadMore : undefined}
            hasMore={isColours ? hasMore : undefined}
          />
        )}
      </div>
    </div>
  );
};

export default ProductSettings;
